<template>
  <div v-if="renderMain" class="overall-page">

  <div v-if="headsUp" class="headsup-banner" @click="headsUp = false">
    🔔 A new tournament is starting soon — head to chat and <b>!fight</b> to enter! <span class="hu-dismiss">(click to dismiss)</span>
  </div>

  <BotDownBanner :last-mod="isLiveTournament ? tournamentLastMod : null" />

  <SiteNav active="bracket">
    <template #actions>
      <span class="snav-act">
        <button class="settings-gear" @click="showSettings = true" aria-label="Settings">⚙</button>
        <span class="act-tip">Highlight favorite players with colors, notification sounds, display toggles — and replay the intro tour.</span>
      </span>
      <NavLogin :username="prefs.myUsername" />
      <span class="snav-act">
        <button class="help-button" @click="showHelp = true" aria-label="Help">?</button>
        <span class="act-tip">A searchable FFTBG guide — every chat command and the game mechanics behind the numbers.</span>
      </span>
      <span v-if="canPredict" class="snav-act">
        <button class="predict-toggle" :class="{ on: predictMode }" @click="togglePredict" aria-label="Predict the bracket">🔮</button>
        <span class="act-tip">Predict-the-Bracket: pick the winner of every match and see how you score — against the results and the battle sim.</span>
      </span>
      <span class="snav-act">
        <a class="generator-button" href="?build&random" aria-label="Random unit generator">🎲</a>
        <span class="act-tip">Roll a random FFTBG unit in the Builder — force or ban classes and skills, then tweak anything.</span>
      </span>
      <span v-if="previous_tournament_id && !isArchivedTournament" class="snav-act">
        <a class="prev-tourney-button" :href="'/?tournament=' + previous_tournament_id" aria-label="Previous tournament">⏮</a>
        <span class="act-tip">Jump back to the previous tournament's final bracket.</span>
      </span>
    </template>
  </SiteNav>

  <WhatsNewModal v-if="showWhatsNew" @close="closeWhatsNew" @tour="startMainTour" />
  <SpotlightModal v-if="showSpotlight" :updates="spotlightUpdates" @close="closeSpotlight" />
  <OnboardingTour ref="tour" :steps="tourSteps" @done="onTourDone" />

  <HelpModal v-if="showHelp" @close="showHelp = false" />

  <UnitCardModal v-if="fullCard"
                 :unit="fullCard.unit" :team-color="fullCard.teamColor"
                 :enemy-team="fullCard.enemyTeam" :own-team="fullCard.ownTeam"
                 :class-rank="classRank" :tool-tips="toolTips"
                 :not-ables="notAbles" :char-stats="charStats" :itemschanged="itemschanged"
                 :abilities-info="abilitiesInfo" :highlight-colors="highlightColors"
                 :show-win-rates="prefs.showWinRates"
                 @close="fullCard = null" />

  <SettingsModal v-if="showSettings"
                 :prefs="prefs"
                 @update:prefs="onPrefsUpdate"
                 @close="showSettings = false" />

  <AnnouncementModal v-if="showAnnouncement"
                     :matches="announcementMatches"
                     :skill-drop="skillDropInfo"
                     :settings-hint="announceSettingsHint"
                     :settings-link="true"
                     @dismiss="dismissAnnouncement"
                     @open-settings="openSettingsFromAnnouncement"
                     @scroll-to-player="gotoPlayer" />

  <!-- Floating "my skill modifiers" button + modal — only when the viewer has set
       a handle. Shows the !skills-style breakdown of their owned skills' roll mods. -->
  <button v-if="prefs.myUsername" class="my-mods-fab" @click="showMyMods = true"
          title="Your active skill modifiers — how your owned skills nudge your character roll">⚡ My Mods</button>
  <MyModifiersModal v-if="showMyMods" :handle="prefs.myUsername" :champion="nextChampion" @close="showMyMods = false" />

  <FightModal v-if="fightTeams"
              :map="fightTeams.map"
              :left-team="teamByName[fightTeams.left]"
              :right-team="teamByName[fightTeams.right]"
              :itemschanged="itemschanged"
              :char-stats="charStats"
              :abilities-info="abilitiesInfo"
              :not-ables="notAbles"
              :tool-tips="toolTips"
              :predict="fightPredict"
              @pick-winner="onPick(fightTeams.matchIndex, $event)"
              @close="fightTeams = null" />

  <div class="container">
    <div id="app">
      <div v-if="testMode" class="test-mode-banner">TEST MODE — synthetic units. Visit / to return.</div>
      <div v-if="isArchivedTournament" class="archive-strip">
        <span class="archive-label">⏪ Viewing an archived tournament</span>
        <a v-if="previous_tournament_id" class="archive-btn" :href="'/?tournament=' + previous_tournament_id">⏮ Prev</a>
        <a v-if="next_tournament_id" class="archive-btn" :href="'/?tournament=' + next_tournament_id">Next ⏭</a>
        <a class="archive-btn archive-live" href="/">● Back to Live</a>
      </div>
      <RecentTicker v-if="!testMode && prefs.showTicker !== false" />

      <div v-if="goalWidget" class="goal-widget">
        <div class="goal-head">
          <button class="goal-dismiss" @click="dismissGoal" title="Hide this widget — visit your profile to show it again">✕</button>
          <span class="goal-label">🎯 {{ goalWidget.name }}</span>
          <span class="goal-status">{{ goalWidget.status }}</span>
          <a class="goal-profile" :href="'?player=' + encodeURIComponent(prefs.myUsername) + '&tab=achievements'">profile →</a>
        </div>
        <div v-if="goalWidget.rolling" class="goal-rolling">✨ You're rolling a <b>{{ goalWidget.rolling }}</b> right now — that's one you need!</div>
        <div v-if="goalWidget.remaining.length" class="goal-remaining">
          <span class="goal-remain-label">Still need:</span>
          <span v-for="r in goalWidget.remaining.slice(0, 12)" :key="r" class="goal-remain-item" :class="{ hit: r === goalWidget.rolling }">{{ r }}</span>
          <span v-if="goalWidget.remaining.length > 12" class="goal-remain-more">+{{ goalWidget.remaining.length - 12 }} more</span>
        </div>
        <div v-else-if="!goalWidget.rolling" class="goal-hint">Keep playing — {{ goalWidget.status }}</div>
      </div>

<div v-if="!prefs.hideBracket" class="bracket-holder">
  <PredictPanel v-if="predictMode" :score="predictScore" :sim="predictSimRecord" :lifetime="predictState.lifetime" :complete="currentMatchIndex === -1 && predictScore.picksMade > 0" />
  <div class="tournament-bracket tournament-bracket--rounded" :class="activeRound ? 'active-' + activeRound : ''">

    <div class="tournament-bracket__round tournament-bracket__round--quarterfinals">
      <h3 class="tournament-bracket__round-title">Quarterfinals</h3>
      <ul class="tournament-bracket__list">
        <BracketMatch v-bind="bracketSharedProps"
          :map="maps[0]" :is-current="currentMatchIndex === 0" :match-index="0" :predict="predictMode ? predictCells[0] : null" @pick="onPick(0, $event)" vs-title="Red vs. Blue"
          :left-teams="[{name:'red',  show:true, enemyColor:teamByName.blue.label}]"
          :right-teams="[{name:'blue', show:true, enemyColor:teamByName.red.label}]"
          :left-won="bracketState.redBracketR1WIN"
          :right-won="bracketState.blueBracketR1WIN" />
        <BracketMatch v-bind="bracketSharedProps"
          :map="maps[1]" :is-current="currentMatchIndex === 1" :match-index="1" :predict="predictMode ? predictCells[1] : null" @pick="onPick(1, $event)" vs-title="Green vs. Yellow"
          :left-teams="[{name:'green',  show:true, enemyColor:teamByName.yellow.label}]"
          :right-teams="[{name:'yellow', show:true, enemyColor:teamByName.green.label}]"
          :left-won="bracketState.greenBracketR1WIN"
          :right-won="bracketState.yellowBracketR1WIN" />
        <BracketMatch v-bind="bracketSharedProps"
          :map="maps[2]" :is-current="currentMatchIndex === 2" :match-index="2" :predict="predictMode ? predictCells[2] : null" @pick="onPick(2, $event)" vs-title="White vs. Black"
          :left-teams="[{name:'white', show:true, enemyColor:teamByName.black.label}]"
          :right-teams="[{name:'black', show:true, enemyColor:teamByName.white.label}]"
          :left-won="bracketState.whiteBracketR1WIN"
          :right-won="bracketState.blackBracketR1WIN" />
        <BracketMatch v-bind="bracketSharedProps"
          :map="maps[3]" :is-current="currentMatchIndex === 3" :match-index="3" :predict="predictMode ? predictCells[3] : null" @pick="onPick(3, $event)" vs-title="Purple vs. Brown"
          :left-teams="[{name:'purple', show:true, enemyColor:teamByName.brown.label}]"
          :right-teams="[{name:'brown',  show:true, enemyColor:teamByName.purple.label}]"
          :left-won="bracketState.purpleBracketR1WIN"
          :right-won="bracketState.brownBracketR1WIN" />
      </ul>
    </div>

    <div class="tournament-bracket__round tournament-bracket__round--semifinals">
      <h3 class="tournament-bracket__round-title">Semifinals</h3>
      <ul class="tournament-bracket__list">
        <BracketMatch v-bind="bracketSharedProps" match-class="semi"
          :map="maps[4]" :is-current="currentMatchIndex === 4" :match-index="4" :predict="predictMode ? predictCells[4] : null" @pick="onPick(4, $event)"
          :vs-title="semiOneLeft + ' vs. ' + semiOneRight"
          :left-teams="[
            {name:'red',  show:bracketState.redBracketR1WIN,  enemyColor:semiOneRight},
            {name:'blue', show:bracketState.blueBracketR1WIN, enemyColor:semiOneRight},
          ]"
          :right-teams="[
            {name:'green',  show:bracketState.greenBracketR1WIN,  enemyColor:semiOneLeft},
            {name:'yellow', show:bracketState.yellowBracketR1WIN, enemyColor:semiOneLeft},
          ]"
          :left-won="bracketState.topleftBracketR2WIN"
          :right-won="bracketState.toprightBracketR2WIN" />
        <BracketMatch v-bind="bracketSharedProps" match-class="semi"
          :map="maps[5]" :is-current="currentMatchIndex === 5" :match-index="5" :predict="predictMode ? predictCells[5] : null" @pick="onPick(5, $event)"
          :vs-title="semiTwoLeft + ' vs. ' + semiTwoRight"
          :left-teams="[
            {name:'white', show:bracketState.whiteBracketR1WIN, enemyColor:semiTwoRight},
            {name:'black', show:bracketState.blackBracketR1WIN, enemyColor:semiTwoRight},
          ]"
          :right-teams="[
            {name:'purple', show:bracketState.purpleBracketR1WIN, enemyColor:semiTwoLeft},
            {name:'brown',  show:bracketState.brownBracketR1WIN,  enemyColor:semiTwoLeft},
          ]"
          :left-won="bracketState.bottomleftBracketR2WIN"
          :right-won="bracketState.bottomrightBracketR2WIN" />
      </ul>
    </div>

    <div class="tournament-bracket__round tournament-bracket__round--bronze">
      <h3 class="tournament-bracket__round-title">Finals</h3>
      <ul class="tournament-bracket__list">
        <BracketMatch v-bind="bracketSharedProps" match-class="final"
          :map="maps[6]" :is-current="currentMatchIndex === 6" :match-index="6" :predict="predictMode ? predictCells[6] : null" @pick="onPick(6, $event)"
          :vs-title="finalLeft + ' vs. ' + finalRight"
          :left-teams="[
            {name:'red',    show:bracketState.redBracketR1WIN    && bracketState.topleftBracketR2WIN,  enemyColor:finalRight},
            {name:'blue',   show:bracketState.blueBracketR1WIN   && bracketState.topleftBracketR2WIN,  enemyColor:finalRight},
            {name:'green',  show:bracketState.greenBracketR1WIN  && bracketState.toprightBracketR2WIN, enemyColor:finalRight},
            {name:'yellow', show:bracketState.yellowBracketR1WIN && bracketState.toprightBracketR2WIN, enemyColor:finalRight},
          ]"
          :right-teams="[
            {name:'white',  show:bracketState.whiteBracketR1WIN  && bracketState.bottomleftBracketR2WIN,  enemyColor:finalLeft},
            {name:'black',  show:bracketState.blackBracketR1WIN  && bracketState.bottomleftBracketR2WIN,  enemyColor:finalLeft},
            {name:'purple', show:bracketState.purpleBracketR1WIN && bracketState.bottomrightBracketR2WIN, enemyColor:finalLeft},
            {name:'brown',  show:bracketState.brownBracketR1WIN  && bracketState.bottomrightBracketR2WIN, enemyColor:finalLeft},
          ]"
          :left-won="bracketState.topleftBracketR3WIN"
          :right-won="bracketState.toprightBracketR3WIN" />
      </ul>
    </div>

    <div class="tournament-bracket__round tournament-bracket__round--gold">
      <h3 class="tournament-bracket__round-title">Champ Match</h3>
      <ul class="tournament-bracket__list">
        <div class="champ-cell-host">
          <ColiseumPromo class="cc-promo" />
          <BracketMatch v-bind="bracketSharedProps" match-class="champ"
            :champ-streak="champStreak"
            :map="maps[7]" :is-current="currentMatchIndex === 7" :match-index="7" :predict="predictMode ? predictCells[7] : null" @pick="onPick(7, $event)"
            :vs-title="champLeft + ' vs. Champions'"
            :left-teams="champLeftCandidates"
            :right-teams="[{name:'champion', show:true, enemyColor:champLeft}]"
            :challengers="champChallengers"
            :left-won="bracketState.topleftBracketR4WIN"
            :right-won="bracketState.toprightBracketR4WIN" />
          <div v-if="skillDropInfo" class="skill-drop-chip" :class="'rarity-' + skillDropInfo.rarity"
               :title="'!buyrandom skill drop for this tournament — ' + skillDropInfo.rarity + (skillDropInfo.category ? ' ' + skillDropInfo.category : '')">
            <span class="sd-label">🎁 Skill Drop</span>
            <span class="sd-name">{{ skillDropInfo.name }}</span>
            <span class="sd-rarity">{{ skillDropInfo.rarity }}</span>
            <span v-if="dropOwned !== null" class="sd-own" :class="dropOwned ? 'owned' : 'missing'">{{ dropOwned ? '✓ you own this' : "you're missing this!" }}</span>
            <span v-if="dropLastText" class="sd-last" :class="{ rare: dropLastText.rare }" :title="dropLastText.title || null">{{ dropLastText.rare && !dropLastText.text.startsWith('not') ? '🫨 ' : '' }}{{ dropLastText.text }}</span>
          </div>
        </div>
      </ul>
    </div>

  </div>
</div>





      <div class="top-bar">

      <div class="buttons">
        <button v-for="team in teams" :key="team.name"
                v-on:click="team.hidden = !team.hidden"
                :class="{ hiding: team.hidden }">{{ team.name === 'champion' ? 'Champ' : team.label }}</button>
      </div>

      <div class="snubs-entries">
        <div v-if="highlightedEntrants.length" class="entries">
          <div v-for="u in highlightedEntrants" :key="'in-'+u.name" :style="u.color ? { color: u.color } : null">
            <a href="#" class="entrant-link" @click.prevent="gotoPlayer(u.name)"
               :style="u.color ? { color: u.color } : null">{{ u.name }}</a> is in!
          </div>
        </div>
        <div v-if="highlightedSnubs.length" class="snubs">
          <div v-for="u in highlightedSnubs" :key="'snub-'+u.name" :style="u.color ? { color: u.color } : null">{{ u.name }} was Snubbed.</div>
        </div>
      </div>
      </div>





      
    <div class="teams" :class="hideClasses">
      <div v-for="team in teams" :key="team.name" :ref="team.name" :class="['team', team.name]">
        <h3>{{ team.name === 'champion' ? 'Champ' : team.label }} Team <ChampStreakBadge v-if="team.name === 'champion'" :streak="champStreak" /> <span v-if="teamGrades[team.name]" class="grade-badge hasToolTip" :class="'tier-' + teamGrades[team.name].color">{{ teamGrades[team.name].grade }}<span class="toolTip tr-host"><TeamRatingTip :grade="teamGrades[team.name]" :label="team.label" /></span></span></h3>
        <div class="holder">
          <InlineUnit v-for="unit of team.roster"
                      :key="unit.Name + '|' + unit.Class"
                      :teamColor="team.label" :classRank="classRank" :toolTips="toolTips"
                      :notAbles="notAbles" :charStats="charStats" :unit="unit"
                      :itemschanged="itemschanged"
                      :abilitiesInfo="abilitiesInfo"
                      :highlightColors="highlightColors"
                      :default-expanded="prefs.expandedCards"
                      :show-win-rates="prefs.showWinRates"
                      :enemy-team="opponentRoster(team.name)"
                      :own-team="team.roster"
                      @full="fullCard = $event" />
        </div>
      </div>
    </div>
    



  </div>
  </div>

  </div>
  <div v-else class="loading-screen">
    <h1>Luxor's Wiznaib.us</h1>
    <h2 v-if="appState === 'failed'">Couldn't load required data.</h2>
    <h2 v-else>Currently Awaiting Data…</h2>

    <ul class="source-list">
      <li v-for="src in sourceList" :key="src.key" class="source-row" :class="sources[src.key]">
        <span class="dot"></span>
        <span class="label">{{ src.label }}</span>
        <span class="status">{{ sourceStatus(src.key) }}</span>
      </li>
    </ul>

    <button v-if="appState === 'failed'" @click="reload" class="reload-btn">Reload</button>
  </div>
</template>

<script>


import HoverUnit from './components/hoverUnit.vue';
import InlineUnit from './components/inlineUnit.vue';
import BracketMatch from './components/BracketMatch.vue';
import SettingsModal from './components/SettingsModal.vue';
import HelpModal from './components/HelpModal.vue';
import AnnouncementModal from './components/AnnouncementModal.vue';
import ChampStreakBadge from './components/ChampStreakBadge.vue';
import TeamRatingTip from './components/TeamRatingTip.vue';
import MyModifiersModal from './components/MyModifiersModal.vue';
import FightModal from './components/FightModal.vue';
import UnitCardModal from './components/UnitCardModal.vue';

import SiteNav from './components/SiteNav.vue';
import BotDownBanner from './components/BotDownBanner.vue';
import RecentTicker from './components/RecentTicker.vue';
import ColiseumPromo from './components/ColiseumPromo.vue';
import WhatsNewModal from './components/WhatsNewModal.vue';
import NavLogin from './components/NavLogin.vue';
import SpotlightModal from './components/SpotlightModal.vue';
import OnboardingTour from './components/OnboardingTour.vue';
import PredictPanel from './components/PredictPanel.vue';
import { MATCH_DEFS, ROUND_LABEL, matchCandidates, scorePredictions, simRecord, loadPredictions, savePredictions, tournamentRecord, setPick, isActive, finalizeTournament } from './lib/predictions.js';
import { prepTeam } from './lib/simPrep.js';
import { runBattles } from './lib/battleSim.js';
import { calibratedOdds } from './lib/simCalibrationLive.js';
import battleConfig from './data/battleConfig.json';
import { SITE_UPDATES } from './data/siteUpdates.js';
import { onboardSeen, markOnboardSeen, introForced, resetOnboarding } from './lib/onboarding.js';
import { playEventIfEnabled, teamsWithTrackedUnits, matchEvents } from './lib/sounds.js';
import { buildCharStats, buildItemsChanged, buildAbilitiesInfo, buildNotables } from './lib/apiData.js';

import { loadPrefs, savePrefs, markAnnouncementSeen, resolveHighlightedUsers } from './lib/userPrefs.js';
import { freshGoalInfo } from './lib/goalRefresh.js';

// Baked-in highlighted users (Luxor's own crew). Per-user prefs can extend
// or hide this list — see DEFAULT_PREFS / SettingsModal.

import noteables from "./data/notables.json";
import classRanks from "./data/classrank.json";
import { rosterStats } from "./lib/preload.js";
import genTokens from "./data/genTokens.json";
import { fetchDump, bustDump, fetchChampStreak, parseSkills, ownsToken } from "./lib/fftbgLive.js";
import { lastDropAgo } from "./lib/skillDrops.js";
import { teamGrade, unitGrade } from "./lib/unitGrade.js";

// Bundled game-data snapshots — the AUTHORITATIVE source for class stats,
// item stats, and ability info (no live fetch; the old mustad.io API was
// re-publishing FFTBG's own help files with lag). Refresh with
// `node tools/fetchGameData.mjs` (runs automatically in `npm run rebuild`).
import classesBackup from "./data/classes.backup.json";
import itemsBackup from "./data/items.backup.json";
import abilitiesBackup from "./data/abilities.backup.json";
import tipsBackup from "./data/tips.backup.json";
import monsterSkills from "./data/monsterSkills.json";
import mapTerrain from "./data/mapTerrain.json";
import mapTiles from "./data/mapTiles.json";
import { tilesForMap } from "./lib/fftPath.js";

import { buildTestTeams } from "./testFixtures.js";

import axios from "axios";

// Item/class normalization, safeDict, defaults, and the build helpers live in
// ./lib/apiData.js (imported above) so the offline tools build identical dicts.

const safeNotables = buildNotables(noteables);
const bundledCharStats = buildCharStats(classesBackup.classes);
const bundledItemsChanged = buildItemsChanged(itemsBackup.items);
const bundledAbilitiesInfo = buildAbilitiesInfo(abilitiesBackup.abilities);

export default {
  name: "App",
  components: {
    HoverUnit,
    InlineUnit,
    BracketMatch,
    ChampStreakBadge,
    TeamRatingTip,
    MyModifiersModal,
    SettingsModal,
    AnnouncementModal,
    HelpModal,
    FightModal,
    UnitCardModal,

    SiteNav,
    BotDownBanner,
    RecentTicker,
    ColiseumPromo,
    WhatsNewModal,
    NavLogin,
    SpotlightModal,
    OnboardingTour,
    PredictPanel,
  },
  data() {
    return {
      classRank: classRanks,
      // Per-browser user prefs (loaded from localStorage on mount). Shape
      // is defined in src/lib/userPrefs.js — currently:
      //   hideDefaultHighlights, hideBracket, customHighlights[]
      prefs: loadPrefs(),
      showSettings: false,
      showHelp: false,
      // { unit, teamColor } when the full-screen unit card is open, else null.
      fullCard: null,
      showAnnouncement: false,
      // The floating "my skill modifiers" modal (viewer = prefs.myUsername).
      showMyMods: false,
      // The tournament's !buyrandom skill-drop token (e.g. "BraveBoost"), from
      // the API's SkillDrop field. Resolved to name/category/rarity via genTokens.
      skillDrop: null,
      // Whether the viewer (prefs.myUsername) already owns the drop token, from
      // their live userskills dump. null = unknown/not-checked (no handle set, or
      // the fetch is gated/failed — only works on the wiznaib.us origin).
      dropOwned: null,
      // The current champion's title-defense streak (global streak.txt), fetched
      // once per tournament. 0 = unknown/none.
      champStreak: 0,
      // How long since the current drop token last dropped, from the fftbg.com
      // tournaments list: { ago, lastMod } | { capped, window } | null.
      dropLastSeen: null,
      // Fight view: the two team names to compare, or null when closed.
      fightTeams: null,
      // When the live tournament last changed (the API's LastMod): the bot-down banner
      // uses it to tell whether wiznaibot is hearing the matches being played.
      tournamentLastMod: null,
      // "New tournament starting soon" heads-up banner + its poll timers.
      headsUp: false,
      pollTimer: null,
      goalTimer: null,
      headsUpTimer: null,
      // Last poll snapshot { matchIndex, winners } for match-sound detection.
      soundSnapshot: null,
      // Whether the URL is the live tournament (no ?tournament= override).
      // The start-of-tournament announcement only fires for live loads —
      // re-opening an archived tournament link shouldn't surprise the user
      // with a "X is in!" popup for events that already happened.
      isLiveTournament: !new URLSearchParams(window.location.search).get('tournament'),
      items: [],
      // The nine fixed FFTBG teams. Each entry holds its roster (the API's
      // Teams[name].Units), a `hidden` flag (toggled by the button row, the
      // bracket-click handlers, and the auto-hide-on-loss watcher), a `label`
      // (used as HoverUnit/InlineUnit's teamColor prop — capitalized
      // "Red"/"Blue"/.../"Champion") and a `cssShort` used by the existing
      // CSS classes `hidered`, `hideblue`, …, `hidechamp`. Order is the
      // bracket order (QF1 left/right, QF2 left/right, etc.).
      teams: [
        { name: 'red',      cssShort: 'red',    label: 'Red',      hidden: false, roster: [], palettes: '' },
        { name: 'blue',     cssShort: 'blue',   label: 'Blue',     hidden: false, roster: [], palettes: '' },
        { name: 'green',    cssShort: 'green',  label: 'Green',    hidden: false, roster: [], palettes: '' },
        { name: 'yellow',   cssShort: 'yellow', label: 'Yellow',   hidden: false, roster: [], palettes: '' },
        { name: 'white',    cssShort: 'white',  label: 'White',    hidden: false, roster: [], palettes: '' },
        { name: 'black',    cssShort: 'black',  label: 'Black',    hidden: false, roster: [], palettes: '' },
        { name: 'purple',   cssShort: 'purple', label: 'Purple',   hidden: false, roster: [], palettes: '' },
        { name: 'brown',    cssShort: 'brown',  label: 'Brown',    hidden: false, roster: [], palettes: '' },
        { name: 'champion', cssShort: 'champ',  label: 'Champion', hidden: false, roster: [], palettes: '' },
      ],
      entrants: [],
      // Game metadata, built from the bundled snapshots at module load (see
      // the imports above) — available synchronously, no network involved.
      charStats: bundledCharStats,
      itemschanged: bundledItemsChanged,
      // The bundled /api/tips snapshot (hover-card tooltip text) — same shape
      // the live endpoint served: {Zodiac, Class, UserSkill, Ability, Item,
      // MonsterSkills}. Refreshed by tools/fetchGameData.mjs.
      toolTips: tipsBackup,
      maps: [],
      current_tournament_id : "",
      previous_tournament_id : "",
      next_tournament_id : "",
      winners: [],
      // Predict-the-Bracket: whether pick mode is on + the persisted predictions
      // state (lifetime + per-tournament picks), loaded from localStorage.
      predictMode: false,
      predictState: loadPredictions(),
      // Per-API load state: 'loading' | 'loaded' | 'failed'. The tournament
      // (the only required source) gates the main render via appState; the
      // optional recent-list loads in the background and degrades gracefully.
      // (Class/item/ability/tooltip data is all bundled — see charStats and
      // toolTips above — so none of it appears here.)
      sources: {
        tournament: 'loading',
        recent: 'loading',
      },
      sourceErrors: {
        tournament: '', recent: '',
      },
      // Display order + labels for the loading screen.
      sourceList: [
        { key: 'tournament', label: 'Tournament Data (fftbg)' },
        { key: 'recent', label: 'Previous Tournaments (fftbg)' },
      ],
      snubs: [],
      // Map of ability name -> `info` string, used by InlineUnit to
      // derive ability damage formulas (lib/abilityFormula.js).
      abilitiesInfo: bundledAbilitiesInfo,
      IMG_URL : "https://luxor-fftbg-assets.s3.amazonaws.com/units/",
      IMG_URL_ITEMS : "https://luxor-fftbg-assets.s3.amazonaws.com/icons/",
      IMG_URL_MAPS : "https://luxor-fftbg-assets.s3.amazonaws.com/maps/",
      notAbles : safeNotables,
      // True when the page is loaded with ?test=1. Skips the tournament API
      // and synthesizes a fixture-tournament via testFixtures.js.
      testMode: false,
      // The actual gate for showing the main page. Set 150ms after appState
      // becomes 'ready' so the user sees the last source's dot flip green
      // before the v-if/v-else swap removes the loading screen.
      renderMain: false,
      showWhatsNew: false,
      showSpotlight: false,
      forceSpotlight: false,
      tourSteps: [
        { target: '.tournament-bracket', title: 'The bracket', body: 'Hover any unit for a quick card. On any matchup, ⚔ Fight View opens a damage matrix + battle sim, and the % button gives instant win odds.' },
        { target: '.teams', title: 'Unit cards', body: 'Click a unit to expand it in place; the "Full card" button opens its complete detail — stats, gear, abilities, and grades.' },
        { target: '.snav-champions', title: '🏆 Champions', body: 'The Tournament of Champions — a live leaderboard by title-defense streak, with a seeded bracket and sim odds.' },
        { target: '.snav-players', title: '👥 Players', body: '17k-player directory: quests, boards, achievements, career stats, and full championship histories. Player profiles with goal tracking.' },
        { target: '.snav-maps', title: '🗺️ Maps', body: 'All 138 battle maps — which classes the terrain favors, a 3D terrain viewer, and a height-calculator targeting tool.' },
        { target: '.snav-gear', title: '🔍 Gear Lab', body: 'Search any item or pair: how often it rolls, its win rate, top classes, and what it pairs with.' },
        { target: '.snav-stats', title: '📊 Stats', body: 'Deep win-rate analytics — by class, skill, gear, Brave/Faith, and archetype, with trends over time.' },
        { target: '.settings-gear', title: '⚙ Settings', body: 'Set your username for a 👤 profile button + goal tracker, highlight favorite players, toggle notification sounds, and replay this tour.' },
        { target: '.generator-button', title: '🎲 Random generator', body: 'Jump to the Builder with a random FFTBG unit — force or ban classes, then edit anything.' },
        { target: '.help-button', title: '❔ Guide', body: 'A searchable FFTBG reference — every chat command and the game mechanics behind the numbers.' },
      ],
      TOURNAMENT_API_URL : "https://fftbg.com/api/tournament/latest",
      TOURNAMENT_API_PREFIX : "https://fftbg.com/api/tournament/",
    };
  },

  computed: {
    // Feature spotlight: updates the user hasn't seen yet.
    unseenUpdates() {
      const seen = new Set(this.prefs.seenUpdates || []);
      return SITE_UPDATES.filter((u) => u.spotlight !== false && !seen.has(u.id));
    },
    // What to show in the spotlight modal: unseen updates, or all spotlightable if ?spotlight forced.
    spotlightUpdates() {
      const spotlightable = SITE_UPDATES.filter((u) => u.spotlight !== false);
      return this.forceSpotlight && !this.unseenUpdates.length ? spotlightable : this.unseenUpdates;
    },
    // Goal tracker widget — reads from prefs (set on the player profile page).
    goalWidget() {
      const g = this.prefs.activeGoal, info = this.prefs.goalInfo;
      if (!g || !info || !this.prefs.myUsername || this.prefs.goalDismissed) return null;
      // Is the user rolling a remaining target right now? Gendered quests list "Lancer ♂",
      // the others the bare class, and the zodiac quest signs. Carried by Monsters counts
      // teammates' monsters, never your own, so your own unit can't be one it needs.
      let rolling = null;
      if (info.remaining && info.remaining.length && !(g.type === 'quest' && g.id === 'carried')) {
        const byLower = new Map(info.remaining.map((r) => [r.toLowerCase(), r]));
        for (const { unit } of this.findUnits(this.prefs.myUsername)) {
          const labels = g.type === 'quest' && g.id === 'zodiac' ? [unit.Sign || '']
            : [unit.Class, unit.Class + (unit.Gender === 'Male' ? ' ♂' : unit.Gender === 'Female' ? ' ♀' : '')];
          rolling = labels.map((l) => byLower.get(l.toLowerCase())).find(Boolean) || null;
          if (rolling) break;
        }
      }
      return { name: info.name, status: info.status, remaining: info.remaining || [], rolling };
    },
    // Team grade (mean of unit grades) per team — shown on the team-grid headers.
    teamGrades() {
      const gd = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles };
      const out = {};
      for (const t of this.teams) out[t.name] = teamGrade(t.roster, rosterStats, gd);
      return out;
    },
    // 'loading' until the tournament (the only required source) resolves;
    // 'failed' if it errors. Optional sources (tips, recent) don't block.
    // Class/item/ability data is bundled, so it can't fail or delay.
    appState() {
      if (this.sources.tournament === 'failed') return 'failed';
      if (this.sources.tournament === 'loaded') return 'ready';
      return 'loading';
    },
    // True when the URL pins a specific (?tournament=) archived tournament
    // rather than the live one — drives the archive-nav strip (Prev/Next/Live).
    isArchivedTournament() { return !this.isLiveTournament && !!this.current_tournament_id; },
    // Map a team's `name` to its team object — used everywhere we need to
    // look up roster/hidden/label by team name (e.g. allunits[colorName]
    // in HoverUnit, the bracket cells' won/lost class bindings).
    teamByName() {
      const map = {};
      for (const t of this.teams) map[t.name] = t;
      return map;
    },
    // The champion an entry made now would face, for My Mods' VIP helper: whoever wins this
    // tournament's champion match. Until that's played it's the champion, if they hold the belt.
    nextChampion() {
      const w = this.winners[7], won = w && w !== 'champion' && this.teamByName[w];
      if (won) return { units: won.roster, label: won.label, decided: true, isNew: true };
      const champ = this.teamByName.champion;
      return { units: champ ? champ.roster : [], label: 'Champion', decided: !!w, isNew: false };
    },
    // Derives every {teamName}BracketR1WIN / {position}BracketR{2,3,4}WIN flag
    // from the winners array (was previously a 130-line imperative watcher
    // setting each flag on this.). Keys match the previous data-field names
    // exactly so the bracket template's bindings only need to swap
    // `redBracketR1WIN` → `bracketState.redBracketR1WIN`.
    bracketState() {
      const w = this.winners;
      const qfPairs = [['red','blue'], ['green','yellow'], ['white','black'], ['purple','brown']];
      const state = {};
      qfPairs.forEach((pair, i) => {
        pair.forEach(name => { state[name + 'BracketR1WIN'] = w[i] === name; });
      });
      state.topleftBracketR2WIN     = w[4] === 'red'    || w[4] === 'blue';
      state.toprightBracketR2WIN    = w[4] === 'green'  || w[4] === 'yellow';
      state.bottomleftBracketR2WIN  = w[5] === 'white'  || w[5] === 'black';
      state.bottomrightBracketR2WIN = w[5] === 'purple' || w[5] === 'brown';
      state.topleftBracketR3WIN     = ['red','blue','green','yellow'].includes(w[6]);
      state.toprightBracketR3WIN    = ['white','black','purple','brown'].includes(w[6]);
      state.topleftBracketR4WIN     = ['red','blue','green','yellow','white','black','purple','brown'].includes(w[7]);
      state.toprightBracketR4WIN    = w[7] === 'champion';
      return state;
    },
    // Teams still alive in the bracket (not yet eliminated) — for the champ cell's
    // "threat board". Empty once the Final resolves (the opponent is then known, so
    // the cell's own % odds takes over) or the champ match is decided.
    champChallengers() {
      const w = this.winners;
      if (w[6] || w[7]) return [];
      const qf = [['red', 'blue'], ['green', 'yellow'], ['white', 'black'], ['purple', 'brown']];
      const out = new Set();  // eliminated
      qf.forEach((pair, i) => { if (w[i]) out.add(pair[0] === w[i] ? pair[1] : pair[0]); });
      if (w[4]) out.add(w[0] === w[4] ? w[1] : w[0]);
      if (w[5]) out.add(w[2] === w[5] ? w[3] : w[2]);
      return ['red', 'blue', 'green', 'yellow', 'white', 'black', 'purple', 'brown'].filter((t) => !out.has(t));
    },
    // Per-bracket-position labels (Capitalized "Red"/"Blue"/…), used as
    // HoverUnit's `enemyColor` prop in the SF / Finals / Champ cells.
    // Each derives from the corresponding winners[N] slot — replaces what
    // were imperative `this.semiOneLeft = "Red"` lines in the
    // winners watcher.
    semiOneLeft()  { return this.teamByName[this.winners[0]]?.label || ''; },
    semiOneRight() { return this.teamByName[this.winners[1]]?.label || ''; },
    semiTwoLeft()  { return this.teamByName[this.winners[2]]?.label || ''; },
    semiTwoRight() { return this.teamByName[this.winners[3]]?.label || ''; },
    finalLeft()    { return this.teamByName[this.winners[4]]?.label || ''; },
    finalRight()   { return this.teamByName[this.winners[5]]?.label || ''; },
    champLeft()    { return this.teamByName[this.winners[6]]?.label || 'Champion'; },
    // The tournament's skill drop resolved to { token, name, category, rarity }.
    // Falls back to a spaced token name + common rarity for any unmapped token.
    skillDropInfo() {
      if (!this.skillDrop) return null;
      const meta = genTokens.byToken[this.skillDrop];
      const name = meta?.name || this.skillDrop.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
      return { token: this.skillDrop, name, category: meta?.category || '', rarity: meta?.rarity || 'common' };
    },
    // "last dropped N tournaments ago" line for the chip. A long gap (or no prior
    // drop in the window) reads as a rare event — flagged via `.rare` styling.
    dropLastText() {
      const d = this.dropLastSeen;
      if (!d) return null;
      if (d.capped) return { text: `not dropped in ${d.window}+ tournaments`, rare: true, title: '' };
      return {
        text: `last dropped ${d.ago} tournament${d.ago === 1 ? '' : 's'} ago`,
        rare: d.ago >= 200,
        title: d.lastMod ? 'Previously dropped ' + new Date(d.lastMod).toLocaleDateString() : '',
      };
    },
    // The combined set of highlighted users — defaults (Luxor + friends, unless
    // toggled off) plus the user's customHighlights, deduped by lowercased name
    // (custom overrides default). Each is { name, color }; defaults carry the
    // default highlight color so they're visible out of the box.
    highlightedUsers() {
      return resolveHighlightedUsers(this.prefs);
    },
    // Lowercase the entrants/snubs once so each highlight match is a cheap
    // Set lookup instead of repeatedly toLowercase'ing on every comparison.
    // The fftbg API returns usernames with the joining user's display-name
    // casing, which may not match what the modal-user typed — so we
    // compare in lowercase on both sides.
    entrantsLower() { return new Set(this.entrants.map(e => (e || '').toLowerCase().replace(/_/g, ' '))); },
    snubsLower()    { return new Set(this.snubs.map(s    => (s || '').toLowerCase().replace(/_/g, ' '))); },
    // Highlighted users who are entered in the current tournament, in the
    // same order as `highlightedUsers`. Drives the "X is in!" callouts.
    highlightedEntrants() {
      return this.highlightedUsers.filter(u => this.entrantsLower.has(u.name));
    },
    // Same idea for snubs.
    highlightedSnubs() {
      return this.highlightedUsers.filter(u => this.snubsLower.has(u.name));
    },
    // Combined entrant + snub matches for the start-of-tournament
    // announcement modal. Each entry carries a `status` field so the
    // modal can pick "is in!" vs "was Snubbed.".
    announcementMatches() {
      return [
        ...this.highlightedEntrants.map(u => ({ ...u, status: 'in', units: this.findUnits(u.name) })),
        ...this.highlightedSnubs.map(u    => ({ ...u, status: 'snubbed' })),
      ];
    },
    // Footer hint on the announcement modal pointing fresh visitors at the
    // ⚙ Settings — only while their highlight prefs are untouched (anyone
    // who's added a highlight or set their username has already found it).
    announceSettingsHint() {
      return !this.prefs.customHighlights.length && !this.prefs.myUsername;
    },
    // Map of lowercase-username → color for every highlighted user (defaults
    // included — they now carry the default highlight color), passed as a prop
    // to BracketMatch/InlineUnit/HoverUnit so they color the unit-name text
    // wherever it renders. Deduped via highlightedUsers.
    highlightColors() {
      const m = new Map();
      for (const u of this.highlightedUsers) if (u.color) m.set(u.name, u.color);
      return m;
    },
    // CSS-class object for the outer .teams div — toggles `hidered`,
    // `hideblue`, …, `hidechamp` based on each team's hidden flag. Matches
    // the existing CSS selectors (assets/css/main.scss).
    hideClasses() {
      const out = {};
      for (const t of this.teams) out['hide' + t.cssShort] = t.hidden;
      return out;
    },
    // Index (0-7) of the next undecided match — the one currently being
    // played — used to glow that bracket cell. -1 once every match is
    // decided (so completed/archived tournaments don't glow). Match order:
    // QF 0-3, SF 4-5, Final 6, Champ 7 (matches the maps/winners indices).
    currentMatchIndex() {
      for (let i = 0; i < 8; i++) {
        if (!this.winners[i]) return i;
      }
      return -1;
    },
    // Which round the current match is in — drives the enlarged-column styling.
    activeRound() {
      const i = this.currentMatchIndex;
      if (i < 0) return null;
      if (i <= 3) return 'qf';
      if (i <= 5) return 'sf';
      if (i === 6) return 'final';
      return 'champ';
    },
    // ---- Predict-the-Bracket ----
    // Pick mode is offered on the live bracket + the ?test=1 fixture, not on
    // archived (?tournament=) views (predicting a decided tournament is moot).
    canPredict() { return this.isLiveTournament; },
    // This tournament's prediction record (read-only here; created on first pick).
    predictRec() {
      const id = this.current_tournament_id;
      return (id && this.predictState.byTournament[id]) || { picks: {}, sim: {} };
    },
    // Per-cell predict state (match-index order) handed to each <BracketMatch>.
    predictCells() {
      const rec = this.predictRec, w = this.winners;
      return MATCH_DEFS.map((def) => {
        const winner = w[def.i] || null;
        return {
          i: def.i, weight: def.weight, roundLabel: ROUND_LABEL[def.round],
          candidates: matchCandidates(def.i, w, rec.picks),
          picked: rec.picks[def.i] || null,
          simPick: winner ? (rec.sim[def.i] || null) : null, // revealed only once decided
          winner,
        };
      });
    },
    // Running scorecard for the PredictPanel.
    predictScore() { return scorePredictions(this.predictRec.picks, this.winners); },
    // You-vs-the-sim head-to-head (over decided matches where both have a pick).
    predictSimRecord() { return simRecord(this.predictRec.picks, this.predictRec.sim, this.winners); },
    // Predict state for the matchup open in the Fight View (so it offers a pick
    // affordance there too), or null when not predicting / no match context.
    fightPredict() {
      if (!this.predictMode || !this.fightTeams || this.fightTeams.matchIndex == null) return null;
      return this.predictCells[this.fightTeams.matchIndex];
    },
    // Bundle of props every <BracketMatch> needs but doesn't vary per cell —
    // spread via v-bind so each call site is just the cell-specific bits.
    bracketSharedProps() {
      return {
        teamByName: this.teamByName,
        items: this.items,
        notAbles: this.notAbles,
        charStats: this.charStats,
        itemschanged: this.itemschanged,
        abilitiesInfo: this.abilitiesInfo,
        toolTips: this.toolTips,
        imgUrlMaps: this.IMG_URL_MAPS,
        highlightColors: this.highlightColors,
        showWinRates: this.prefs.showWinRates,
        attackAnims: this.prefs.attackAnims,
        onBracketClick: this.bracketClick,
        onGoto: this.goto,
        onFight: this.openFight,
      };
    },
    // Left-side candidates for the Champ match: any of the eight non-champion
    // teams that survived all three rounds. Inlined into the BracketMatch
    // call would be 8 lines of bracketState ANDs; pulled out as a computed
    // to keep the template readable.
    champLeftCandidates() {
      const b = this.bracketState;
      const enemy = this.teamByName.champion.label;
      return [
        { name: 'red',    show: b.redBracketR1WIN    && b.topleftBracketR2WIN     && b.topleftBracketR3WIN,  enemyColor: enemy },
        { name: 'blue',   show: b.blueBracketR1WIN   && b.topleftBracketR2WIN     && b.topleftBracketR3WIN,  enemyColor: enemy },
        { name: 'green',  show: b.greenBracketR1WIN  && b.toprightBracketR2WIN    && b.topleftBracketR3WIN,  enemyColor: enemy },
        { name: 'yellow', show: b.yellowBracketR1WIN && b.toprightBracketR2WIN    && b.topleftBracketR3WIN,  enemyColor: enemy },
        { name: 'white',  show: b.whiteBracketR1WIN  && b.bottomleftBracketR2WIN  && b.toprightBracketR3WIN, enemyColor: enemy },
        { name: 'black',  show: b.blackBracketR1WIN  && b.bottomleftBracketR2WIN  && b.toprightBracketR3WIN, enemyColor: enemy },
        { name: 'purple', show: b.purpleBracketR1WIN && b.bottomrightBracketR2WIN && b.toprightBracketR3WIN, enemyColor: enemy },
        { name: 'brown',  show: b.brownBracketR1WIN  && b.bottomrightBracketR2WIN && b.toprightBracketR3WIN, enemyColor: enemy },
      ];
    },
  },

  watch: {
        // Restore the player's predict-mode opt-in when the tournament id is set
        // (on load) or changes (a new tournament): predict mode reflects whether
        // they turned it on for THIS bracket. A new/unopted bracket starts off.
        current_tournament_id: function(id) {
          this.predictMode = this.canPredict && isActive(this.predictState, id);
          if (this.predictMode) this.$nextTick(() => this.ensureSimPicks());
        },
        // Slight pause between "everything required is loaded" and the
        // loading-screen → main-page swap so the user actually sees the
        // last source's dot flip green before the loading screen unmounts.
        appState: function(newVal) {
          if (newVal === 'ready') {
            // gameData is now loaded — compute any pending sim picks for a bracket
            // whose predict mode was restored before the data was ready.
            if (this.predictMode) this.ensureSimPicks();
            setTimeout(() => {
              this.renderMain = true;
              const forced = introForced();
              if (forced) resetOnboarding();  // ?intro replays everything, incl. the sub-page tours next visit
              this.forceSpotlight = new URLSearchParams(window.location.search).has('spotlight');
              if (forced || !onboardSeen('intro')) {
                this.showWhatsNew = true;  // first visit (or ?intro) → welcome; mark all current updates as seen
              } else if (this.forceSpotlight || this.unseenUpdates.length) {
                this.showSpotlight = true;  // returning user with new features → spotlight
              } else {
                this.maybeShowAnnouncement();
              }
            }, 150);
          }
        },

        // Persist prefs to localStorage on any change — the settings modal
        // mutates a deep clone and emits whole-object updates, but the
        // announcement-dismiss flow mutates seenAnnouncements directly.
        // Watching deep covers both paths.
        prefs: {
          deep: true,
          handler(newVal) { savePrefs(newVal); },
        },

        // Re-check drop ownership + how-long-since-last-dropped when the drop
        // changes (new tournament) or the viewer sets/changes their handle.
        // immediate so it fires on first load.
        skillDrop: { immediate: true, handler() { this.checkDropOwnership(); this.loadDropHistory(); } },
        'prefs.myUsername': function() { this.checkDropOwnership(); },

        winners: function(newVal, oldVal) {
          // Auto-hide losing teams as winners come in. Bracket-WIN flags
          // themselves are derived in bracketState (computed), not here.
          // Live polling reassigns `winners` every 60s; without this guard a
          // team the user manually un-hid would be silently re-hidden each tick.
          if (oldVal && JSON.stringify(newVal) === JSON.stringify(oldVal)) return;
          const w = this.winners;
          const hide = (name) => {
            const t = this.teamByName[name];
            if (t) t.hidden = true;
          };
          const qfPairs = [['red','blue'], ['green','yellow'], ['white','black'], ['purple','brown']];
          qfPairs.forEach((pair, i) => {
            if (w[i] === pair[0]) hide(pair[1]);
            if (w[i] === pair[1]) hide(pair[0]);
          });
          if (w[4] === 'red'    || w[4] === 'blue')   { hide('green');  hide('yellow'); }
          if (w[4] === 'green'  || w[4] === 'yellow') { hide('red');    hide('blue');   }
          if (w[5] === 'white'  || w[5] === 'black')  { hide('purple'); hide('brown');  }
          if (w[5] === 'purple' || w[5] === 'brown')  { hide('white');  hide('black');  }
          if (['red','blue','green','yellow'].includes(w[6])) {
            ['white','black','purple','brown'].forEach(hide);
          }
          if (['white','black','purple','brown'].includes(w[6])) {
            ['red','blue','green','yellow'].forEach(hide);
          }
          // A match just resolved — fill in the sim's pick for it (if predicting).
          this.ensureSimPicks();
        },
    },

  async created() {
    // Quietly prefetch the top-50 champions (small, ~170KB) a few seconds after
    // load, so the 🏆 Champions page opens instantly from sessionStorage.
    setTimeout(() => {
      try {
        if (sessionStorage.getItem('fftbgChampsTop50')) return;
        axios.get('https://fftbg.com/api/champions/?limit=50', { timeout: 12000 })
          .then((r) => { if (Array.isArray(r.data) && r.data.length) sessionStorage.setItem('fftbgChampsTop50', JSON.stringify(r.data)); })
          .catch(() => {});
      } catch (e) { /* sessionStorage unavailable */ }
    }, 2500);
    const urlParams = new URLSearchParams(window.location.search);
    // ?test=1 activates the synthetic-tournament fixture (testFixtures.js).
    // We still hit the classes + items APIs so the test units render with
    // real stats; we just skip tournament/tips/recent and synthesize the
    // tournament shape after items load.
    this.testMode = urlParams.get('test') === '1';

    if (this.testMode) {
      this.sources.tournament = 'loaded';
      this.sources.recent = 'loaded';
    } else {
      this.OLD_TOURNAMENT_ID = urlParams.get("tournament");
      if (this.OLD_TOURNAMENT_ID) {
        this.TOURNAMENT_API_URL = this.TOURNAMENT_API_PREFIX + this.OLD_TOURNAMENT_ID;
      }
      await this.loadTournament();

    try {
      // Derive the prev/next tournament ids from the recent-25 list (newest
      // first). findIndex returns -1 when the current tournament isn't in the
      // list, in which case there's no usable neighbor — leave the fields empty
      // so the prev button + archive strip controls hide. idx+1 is older
      // (previous), idx-1 is newer (next).
      const res = await axios.get(`https://fftbg.com/api/tournaments?limit=25`, { timeout: 10000 });
      const idList = res.data.map(t => t.ID);
      const idx = idList.findIndex(id => id == this.current_tournament_id);
      this.previous_tournament_id = (idx >= 0 && idx + 1 < idList.length) ? idList[idx + 1] : '';
      this.next_tournament_id = (idx > 0) ? idList[idx - 1] : '';
      this.sources.recent = 'loaded';
    } catch (error) {
      this.sources.recent = 'failed';
      this.sourceErrors.recent = error.message || String(error);
    }
    } // end of: if (!this.testMode)



    // Test fixture: itemschanged is bundled, so it's always populated.
    if (this.testMode) {
      const fake = buildTestTeams(this.itemschanged);
      this.current_tournament_id = fake.ID;
      this.items = fake.Teams;
      this.maps = fake.Maps;
      for (const team of this.teams) {
        team.roster = fake.Teams[team.name]?.Units || [];
        team.palettes = fake.Teams[team.name]?.Palettes || '';
      }
      this.winners = fake.Winners;
      this.entrants = fake.Entrants;
      this.snubs = fake.Snubs;
    }

    // Keep the goal widget current: rebuild the saved goal whenever a newer data
    // build is out, and re-check every 10 minutes for a tab left open across refreshes.
    if (!this.testMode) {
      this.refreshGoal();
      this.goalTimer = setInterval(() => this.refreshGoal(), 10 * 60 * 1000);
    }

    // Live tournaments only: poll the bracket every minute so winners,
    // the current-match glow, and the team grid update without a refresh.
    if (!this.testMode && !this.OLD_TOURNAMENT_ID) {
      // Seed the match-sound snapshot from the initial load so the first poll
      // only reacts to changes since now (no sounds for already-decided matches).
      this.soundSnapshot = { matchIndex: this.currentMatchIndex, winners: [...this.winners] };
      this.pollTimer = setInterval(() => this.pollTournament(), 60000);
    }
  },

  beforeUnmount() {
    clearInterval(this.pollTimer);
    clearInterval(this.goalTimer);
    clearTimeout(this.headsUpTimer);
  },

  methods: {

    // Does the viewer already own the tournament's skill drop? Reads their live
    // userskills dump (prefs.myUsername) and checks for the drop token. Sets
    // dropOwned true/false, or leaves it null when there's no handle, no drop, or
    // the fetch is unavailable (the dump's CORS only allows the wiznaib.us origin,
    // so this is a no-op on staging/local). Guards against the drop changing
    // mid-fetch.
    async checkDropOwnership() {
      const me = (this.prefs.myUsername || '').trim();
      const drop = this.skillDrop;
      this.dropOwned = null;
      if (!me || !drop) return;
      const text = await fetchDump('userskills', me);
      if (text == null || this.skillDrop !== drop) return;
      this.dropOwned = ownsToken(parseSkills(text), drop);
    },

    // The current champion's title-defense streak (global, prod-only). Left at 0
    // when the fetch is unavailable so the badge just doesn't show. Archived
    // (?tournament=) views skip it entirely — streak.txt is the LIVE champion's
    // count, which has nothing to do with whoever was champion back then.
    async loadChampStreak() {
      if (!this.isLiveTournament) return;
      const n = await fetchChampStreak();
      if (n != null) this.champStreak = n;
    },

    // How long since the current drop token last dropped. One call to the
    // fftbg.com tournaments list (our normal CORS-friendly API — works on every
    // origin), once per drop change. No prior occurrence in the window → "capped".
    async loadDropHistory() {
      const token = this.skillDrop;
      this.dropLastSeen = null;
      if (!token) return;
      const WINDOW = 300;  // ~12 days; the avg drop gap is ~165, so most are still exact (~164KB vs 273KB at 500)
      try {
        const res = await axios.get(`https://fftbg.com/api/tournaments?limit=${WINDOW}`, { timeout: 10000 });
        if (this.skillDrop !== token) return;  // a new tournament landed mid-fetch
        this.dropLastSeen = lastDropAgo(res.data, token) || { capped: true, window: WINDOW };
      } catch { /* leave null — the line just doesn't show */ }
    },

    // The roster a given team is matched against RIGHT NOW, for the per-spell
    // damage hover on its unit cards. Follows the live bracket rather than the
    // fixed QF pairing: a team's current opponent is the other competitor in the
    // first undecided match it's in. Deeper-round slots resolve from `winners`
    // (SF = QF winners, Final = SF winners, Champ = Final winner vs Champion).
    // Returns [] when the opponent isn't decided yet (or the team is out), so the
    // preview never shows a stale round-1 enemy after the team has advanced.
    opponentRoster(teamName) {
      const w = this.winners || [];
      const matches = [
        ['red', 'blue'], ['green', 'yellow'], ['white', 'black'], ['purple', 'brown'],
        [w[0], w[1]], [w[2], w[3]], [w[4], w[5]], [w[6], 'champion'],
      ];
      const roster = (name) => (name && this.teamByName[name] && this.teamByName[name].roster) || [];
      for (let i = 0; i < matches.length; i++) {
        if (w[i]) continue;                                 // match already decided
        const [a, b] = matches[i];
        if (a === teamName) return roster(b);
        if (b === teamName) return roster(a);
      }
      return [];
    },

    // Fetch the tournament and (re)populate teams/maps/rosters/winners/etc.
    // Used on mount and by the live poll. Returns whether the tournament ID
    // changed (a brand-new tournament appeared).
    async loadTournament() {
      try {
        const res = await axios.get(this.TOURNAMENT_API_URL, { timeout: 10000 });
        const prevId = this.current_tournament_id;
        const changed = !!prevId && res.data.ID !== prevId;
        this.current_tournament_id = res.data.ID;
        if (changed) {
          for (const t of this.teams) t.hidden = false;
        }
        // Rosters/maps/entrants are immutable once the API exposes a tournament
        // (entries lock first), so only swap them on first load or a new ID.
        // Re-assigning fresh identities every poll wiped per-card tuning state
        // (InlineUnit's unit watcher resets on identity change) and re-rendered
        // the whole grid each minute on identical data.
        if (!prevId || changed) {
          this.items = res.data.Teams;
          this.maps = res.data.Maps;
          for (const team of this.teams) {
            team.roster = this.items[team.name]?.Units || [];
            team.palettes = this.items[team.name]?.Palettes || '';   // "Primary/Alt" team colors
          }
          this.entrants = res.data.Entrants;
          this.snubs = res.data.Snubs;
          this.loadChampStreak();  // global champ streak — once per tournament
        }
        // Winners + skill drop are the live parts — but keep the array identity
        // stable too, swapping only when a result actually landed.
        if (JSON.stringify(res.data.Winners) !== JSON.stringify(this.winners)) {
          this.winners = res.data.Winners;
        }
        this.skillDrop = res.data.SkillDrop || null;
        this.tournamentLastMod = res.data.LastMod || null;
        if (changed) {
          // New tournament — the viewer's owned skills may have changed (skills
          // turn over each tournament), so drop the cached read and re-grade the
          // drop against a fresh fetch. (The skillDrop watcher covers the case
          // where the drop token itself also changed; this covers a same-token
          // drop across tournaments.)
          bustDump('userskills', this.prefs.myUsername);
          this.checkDropOwnership();
        }
        this.sources.tournament = 'loaded';
        return { ok: true, changed };
      } catch (error) {
        this.sources.tournament = 'failed';
        this.sourceErrors.tournament = error.message || String(error);
        return { ok: false };
      }
    },

    // One poll tick: refresh the bracket. On a brand-new tournament, re-fire
    // the "X is in!" announcement. When the current tournament just finished,
    // pop the "next one starting soon" heads-up.
    async pollTournament() {
      const wasComplete = this.currentMatchIndex === -1;
      const result = await this.loadTournament();
      if (!result.ok) return;
      this.checkMatchSounds();  // tracked-unit match-up / win / loss / champ
      if (result.changed) {
        this.maybeShowAnnouncement();
        return;
      }
      if (!wasComplete && this.currentMatchIndex === -1) {
        this.announceNewTournamentSoon();
      }
    },

    // Heads-up that the current tournament finished — the next join window is
    // opening. Banner always; sound only if the user enabled that event.
    announceNewTournamentSoon() {
      this.headsUp = true;
      clearTimeout(this.headsUpTimer);
      this.headsUpTimer = setTimeout(() => { this.headsUp = false; }, 45000);
      playEventIfEnabled(this.prefs, 'newTournament');
    },

    // Diff this poll's bracket state against the last one and play a sound for
    // each tracked-unit event (match becoming current, a result, a champ win).
    // Seeds the snapshot on the first call (no sound) so we only react to live
    // changes, not the initial load.
    checkMatchSounds() {
      const snap = { matchIndex: this.currentMatchIndex, winners: [...this.winners] };
      const tracked = new Set(this.highlightedUsers.map((u) => u.name));
      const myTeams = teamsWithTrackedUnits(this.items, tracked);
      if (this.soundSnapshot && myTeams.size) {
        for (const ev of matchEvents(this.soundSnapshot, snap, myTeams)) {
          playEventIfEnabled(this.prefs, ev);
        }
      }
      this.soundSnapshot = snap;
    },


    sourceStatus(key) {
      const s = this.sources[key];
      if (s === 'loaded') return '✓ loaded';
      if (s === 'failed') return `FAILED — ${this.sourceErrors[key] || 'unknown error'}`;
      return 'loading…';
    },

    reload() {
      window.location.reload();
    },

    // Receives the modal's edited prefs and replaces ours. The deep
    // watcher on `prefs` persists the change to localStorage.
    onPrefsUpdate(newPrefs) {
      this.prefs = newPrefs;
    },
    dismissGoal() {
      this.prefs.goalDismissed = true;
      savePrefs(this.prefs);
    },
    // Rebuild the goal widget's saved copy if newer data is out (see lib/goalRefresh.js).
    // The deep prefs watcher saves it.
    async refreshGoal() {
      if (this.prefs.goalDismissed) return;
      const info = await freshGoalInfo(this.prefs).catch(() => undefined);
      if (info !== undefined) this.prefs.goalInfo = info;
    },

    // Called after renderMain flips true. Shows the big "X is in!" /
    // "X was Snubbed!" modal iff: live tournament (not an archived URL,
    // not ?test=1), announcements aren't disabled, we have a current
    // tournament ID we haven't shown the modal for, and at least one
    // highlighted user is in entrants or snubs.
    maybeShowAnnouncement() {
      if (this.testMode) return;
      if (!this.isLiveTournament) return;
      if (this.prefs.disableAnnouncements) return;
      if (!this.current_tournament_id) return;
      if (this.prefs.seenAnnouncements.includes(this.current_tournament_id)) return;
      if (!this.announcementMatches.length) return;
      this.showAnnouncement = true;
    },
    // Find an entrant's unit (+ its team label) across the loaded teams, for the
    // announcement modal's "playing a Knight" line.
    // ALL units a player is fielding this tournament — usually one, but a player
    // who entered AND is the defending champion appears on two teams; the new team
    // is listed first, the Champion slot last. Drives the "X is in!" announcement.
    findUnits(name) {
      const lc = (name || '').toLowerCase();
      const gd = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles };
      const out = [];
      for (const color in this.items) for (const u of (this.items[color]?.Units || [])) if ((u.Name || '').toLowerCase() === lc) out.push({ unit: u, teamLabel: this.teamByName[color]?.label || '', grade: unitGrade(u, rosterStats, gd) });
      out.sort((a, b) => (a.teamLabel === 'Champion' ? 1 : 0) - (b.teamLabel === 'Champion' ? 1 : 0));
      return out;
    },

    // User dismissed the announcement modal — record this tournament as
    // seen so it doesn't re-fire on refresh.
    dismissAnnouncement() {
      this.showAnnouncement = false;
      this.prefs.seenAnnouncements = markAnnouncementSeen(
        this.prefs.seenAnnouncements,
        this.current_tournament_id,
      );
    },

    // "⚙ Settings" link in the announcement footer — dismiss (marks the
    // tournament seen) and open the gear modal.
    openSettingsFromAnnouncement() {
      this.dismissAnnouncement();
      this.showSettings = true;
    },

    closeWhatsNew() {
      this.showWhatsNew = false;
      markOnboardSeen('intro');
      // New visitors who see the Welcome don't need the spotlight — mark all current updates as seen
      this.markAllUpdatesSeen();
      this.maybeShowAnnouncement();
    },
    startMainTour() {
      this.showWhatsNew = false;
      markOnboardSeen('intro');
      this.markAllUpdatesSeen();
      this.$nextTick(() => { if (this.$refs.tour) this.$refs.tour.begin(); });
    },
    closeSpotlight() {
      this.showSpotlight = false;
      this.markAllUpdatesSeen();
      this.maybeShowAnnouncement();
    },
    markAllUpdatesSeen() {
      const seen = new Set(this.prefs.seenUpdates || []);
      for (const u of SITE_UPDATES) seen.add(u.id);
      this.prefs.seenUpdates = [...seen];
      savePrefs(this.prefs);
    },
    onTourDone() { markOnboardSeen('tour.main'); },

    // Open the fight view comparing two teams (by name), from a bracket cell.
    openFight(leftName, rightName, mapName, matchIndex) {
      this.fightTeams = { left: leftName, right: rightName, map: mapName || '', matchIndex: matchIndex == null ? null : matchIndex };
    },

    // ---- Predict-the-Bracket ----
    // Toggling persists the opt-in for THIS bracket so it restores on return
    // (see the current_tournament_id watcher), rather than silently re-surfacing
    // a saved bracket the player didn't knowingly turn on.
    togglePredict() {
      this.predictMode = !this.predictMode;
      const id = this.current_tournament_id;
      if (!id) return;
      const rec = tournamentRecord(this.predictState, id);
      rec.active = this.predictMode;
      savePredictions(this.predictState);
      if (this.predictMode) this.ensureSimPicks();
    },
    // Record (or toggle off) a pick for match `i`; prunes busted downstream picks
    // and persists. No-op once the match is decided (the pick has locked).
    onPick(i, team) {
      const id = this.current_tournament_id;
      if (!id || this.winners[i]) return;
      const rec = tournamentRecord(this.predictState, id);
      setPick(rec, i, rec.picks[i] === team ? null : team, this.winners);
      savePredictions(this.predictState);
    },
    // The sim's favorite for a DECIDED match i — the actual two teams that fought.
    // Mirrors BracketMatch's per-cell % odds (flat Monte-Carlo, champ incumbency edge).
    computeSimFavorite(i) {
      const [left, right] = matchCandidates(i, this.winners, {});
      const lt = this.teamByName[left], rt = this.teamByName[right];
      if (!lt || !rt) return null;
      try {
        const data = { charStats: this.charStats, itemschanged: this.itemschanged, notAbles: this.notAbles, abilitiesInfo: this.abilitiesInfo, monsterSkills };
        // Run on the match's real map geometry (same parse as FightModal / BracketMatch).
        const mt = ((this.maps || [])[i] || '').match(/^\s*(\d+)\)\s*(.*)$/);
        const terrain = (mt && mapTerrain.byName && mapTerrain.byName[mt[2].trim()]) || (mt && mapTerrain.maps ? mapTerrain.maps[mt[1]] : null) || null;
        const r = runBattles(prepTeam(lt.roster, data), prepTeam(rt.roster, data), { ...battleConfig, terrain, tiles: tilesForMap((this.maps || [])[i], mapTiles) });
        const champSide = lt.label === 'Champion' ? 'A' : (rt.label === 'Champion' ? 'B' : null);
        const o = calibratedOdds(r, { champSide });
        return o.a >= o.b ? left : right;
      } catch (e) { return null; }
    },
    // Fill in the sim's favorite for each decided match in the active bracket, one
    // per tick so a backlog (predicting a just-finished tournament) doesn't freeze
    // the page. Each match is attempted once per session; results persist.
    ensureSimPicks() {
      const id = this.current_tournament_id;
      if (!id || !this.predictMode || this._simRunning) return;
      if (this.appState !== 'ready') return; // tournament not loaded yet — retried on appState=ready
      const rec = tournamentRecord(this.predictState, id);
      this._simAttempted = this._simAttempted || new Set();
      const step = () => {
        if (this.current_tournament_id !== id || !this.predictMode) { this._simRunning = false; return; }
        let i = -1;
        for (let k = 0; k < 8; k += 1) {
          if (this.winners[k] && !rec.sim[k] && !this._simAttempted.has(id + ':' + k)) { i = k; break; }
        }
        if (i < 0) { this._simRunning = false; this.maybeFinalize(); return; } // sim picks done → try to finalize
        this._simAttempted.add(id + ':' + i);
        const fav = this.computeSimFavorite(i);
        if (fav) { rec.sim[i] = fav; savePredictions(this.predictState); }
        setTimeout(step, 0);
      };
      this._simRunning = true;
      setTimeout(step, 0);
    },
    // Once a predicted bracket is complete (and its sim picks are in), roll the
    // result into the lifetime record. Idempotent (the lib guards re-finalization).
    maybeFinalize() {
      const id = this.current_tournament_id;
      if (!id || this.currentMatchIndex !== -1) return; // not complete yet
      if (finalizeTournament(this.predictState, id, this.winners)) savePredictions(this.predictState);
    },

    // Clicking a bracket cell un-hides every team so the destination team is
    // visible before we scroll to it.
    bracketClick() {
      for (const team of this.teams) team.hidden = false;
    },

    // Scroll to a team's section in the team-grid. $nextTick gives the DOM a
    // beat to un-hide any teams toggled in bracketClick before we measure.
    goto(name) {
      this.$nextTick(() => {
        const el = document.querySelector('.team.' + name);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    },

    // Announcement-modal player-name click → find which team the player is on
    // and scroll to it. Dismisses the modal first so it doesn't cover the
    // scroll target.
    gotoPlayer(playerName) {
      const lower = (playerName || '').toLowerCase();
      const team = this.teams.find(t =>
        (t.roster || []).some(u => (u.Name || '').toLowerCase() === lower)
      );
      this.dismissAnnouncement();
      if (team) {
        for (const t of this.teams) t.hidden = false;
        this.goto(team.name);
      }
    },

  },
};
</script>

<style scoped>
.loading-screen {
  max-width: 500px;
  margin: 80px auto 0;
  padding: 24px 28px 28px;
  background: rgba(12, 54, 80, 0.55);
  border: 2px solid #fff;
  color: #fff;
  /* Body text in the system sans — BossBattle stays only on the heading. */
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  text-align: left;
}
.loading-screen h1 {
  font-family: "BossBattle";
  color: #fff;
  -webkit-text-stroke: .1px #000;
  font-size: 30px;
  text-align: center;
  margin: 0 0 6px;
}
.loading-screen h2 {
  font-family: inherit;
  color: #fff;
  font-size: 16px;
  font-weight: normal;
  text-align: center;
  margin: 0 0 20px;
  letter-spacing: 0.05em;
}
.source-list {
  list-style: none;
  padding: 0;
  margin: 0 0 22px;
}
.source-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 0;
  font-family: inherit;
  font-size: 14px;
}
.source-row .dot {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  background: #555;
  border: 1px solid #fff;
}
.source-row .label { flex: 1; }
.source-row .status {
  font-size: 12px;
  opacity: 0.9;
}
/* Hard on/off blink — no smooth easing, like a CRT cursor */
.source-row.loading .dot {
  animation: lux-blink 0.9s steps(2, end) infinite;
}
.source-row.loaded .dot {
  background: #00ff00;
}
.source-row.failed .dot {
  background: #ff0000;
}
.source-row.failed .status {
  color: #ff5555;
}
@keyframes lux-blink {
  0%, 50%  { background: #555; }
  51%, 100% { background: #ccc; }
}
.reload-btn {
  display: block;
  margin: 18px auto 0;
  padding: 5px 16px;
  background: #fff;
  color: #2c3e50;
  border: 1px solid #2c3e50;
  font-family: inherit;
  font-size: 14px;
  cursor: pointer;
}
.reload-btn:hover {
  background: #1C6EA4;
  color: #fff;
}

.entrant-link {
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}
.entrant-link:hover { filter: brightness(1.25); }

/* The Champ Match cell + its floaters (the Coliseum promo above it, the skill-drop
   chip below). The host hugs the cell (both are absolutely positioned), so they add
   no layout height and the cell stays vertically aligned with the Finals column. */
.champ-cell-host { position: relative; align-self: center; }

/* Skill-drop chip hanging just below the Champ Match cell (the Coliseum promo has the
   space above it). Rarity colors mirror the FFTBG "User Skills" sheet: green =
   uncommon, blue = rare, neutral = common. */
.skill-drop-chip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 7px 12px;
  width: max-content;
  max-width: 200px;
  border: 2px solid #b9c2cc;
  border-radius: 8px;
  background: #f3f5f7;
  color: #2c3e50;
  text-align: center;
  line-height: 1.2;
}
.skill-drop-chip .sd-label { font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; opacity: 0.7; }
.skill-drop-chip .sd-name { font-weight: bold; font-size: 15px; }
.skill-drop-chip .sd-rarity { font-size: 11px; text-transform: capitalize; opacity: 0.85; }
.skill-drop-chip .sd-own { font-size: 11px; font-weight: bold; margin-top: 3px; padding: 1px 7px; border-radius: 4px; }
.skill-drop-chip .sd-own.owned { color: #2e7d32; background: rgba(46, 125, 50, 0.14); }
.skill-drop-chip .sd-own.missing { color: #b8860b; background: rgba(184, 134, 11, 0.16); }
.skill-drop-chip .sd-last { font-size: 10px; opacity: 0.65; margin-top: 2px; }
.skill-drop-chip .sd-last.rare { opacity: 1; font-weight: bold; color: #b8860b; }

/* The Clockwork Coliseum promo (ColiseumPromo.vue, which styles itself) floats ABOVE the
   Champ Match cell, where there's the most room and it's seen first, with the skill-drop
   chip below. Absolute for the same reason: it adds no layout height, so the cell stays
   aligned with the Finals column. */
.cc-promo {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 12px;
  z-index: 1;
}
/* Just above the row breakpoint the Champ column is narrower than the promo, so centring it
   would push it against the window edge; line it up with the cell's right edge instead (the
   extra width falls over the empty space beside the Finals column). */
@media (min-width: 1200px) and (max-width: 1319px) {
  .cc-promo { left: auto; right: 0; transform: none; }
}

/* Floating "my skill modifiers" button — follows the screen on the bracket page. */
.my-mods-fab {
  position: fixed;
  bottom: 18px;
  right: 18px;
  z-index: 200;
  padding: 9px 16px;
  background: #1c6ea4;
  color: #fff;
  border: 2px solid #7fc4ff;
  border-radius: 22px;
  font-family: inherit;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.4);
}
.my-mods-fab:hover { background: #2e86c1; }
@media (max-width: 600px) {
  .my-mods-fab { bottom: 12px; right: 12px; padding: 8px 13px; font-size: 13px; }
}
.skill-drop-chip.rarity-uncommon { border-color: #6aa84f; background: #d9ead3; }
.skill-drop-chip.rarity-rare     { border-color: #3d78c2; background: #c9daf8; }

/* Below the bracket's row breakpoint (1200px) the rounds stack and the champ
   cell sits directly under its title — no room to FLOAT above it, and only one
   slot below. So drop both floaters into normal flow instead (promo above the
   cell, chip below): each makes its own room and they can't overlap. Column
   alignment doesn't matter here — the rounds are stacked, not side by side. */
@media (max-width: 1199px) {
  .skill-drop-chip,
  .cc-promo {
    position: static;
    transform: none;
  }
  .cc-promo { margin: 0 auto 10px; }
  .skill-drop-chip { margin: 8px auto 0; }
}

</style>

<style>
/* Homepage app-controls — live in the shared nav bar's right-side actions slot */
.snav-act { position: relative; display: inline-flex; align-items: center; }
.snav-act .act-tip {
  display: none; position: absolute; top: calc(100% + 8px); right: 0;
  width: max-content; max-width: 240px;
  background: #0c1c2b; color: #fff; border: 1px solid #4a6f90; border-radius: 6px;
  padding: 8px 10px; font-size: 12px; line-height: 1.4; text-align: left; white-space: normal;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.5); z-index: 402; pointer-events: none;
}
.snav-act:hover .act-tip, .snav-act:focus-within .act-tip { display: block; }
.settings-gear, .help-button, .generator-button, .profile-button, .predict-toggle {
  width: 36px; height: 34px; box-sizing: border-box;
  background: rgba(12, 54, 80, 0.85); color: #fff; border: 2px solid #fff;
  line-height: 1; cursor: pointer; font-family: inherit; padding: 0;
}
.settings-gear { font-size: 21px; }
.help-button { font-size: 21px; }
.generator-button { font-size: 19px; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; }
.profile-button { font-size: 19px; display: inline-flex; align-items: center; justify-content: center; text-decoration: none; }
.predict-toggle { font-size: 19px; }
.login-button {
  height: 34px; box-sizing: border-box; padding: 0 9px; cursor: pointer; font-family: inherit;
  background: #6441a5; color: #fff; border: 2px solid #fff; font-size: 12px; font-weight: bold; white-space: nowrap;
}
.login-button:hover { background: #7d5bbe; }
.predict-toggle.on { background: #7c4dff; border-color: #d6c8ff; box-shadow: 0 0 8px rgba(124, 77, 255, 0.8); }
.settings-gear:hover, .help-button:hover, .generator-button:hover, .profile-button:hover, .predict-toggle:hover { background: #1C6EA4; }
.predict-toggle.on:hover { background: #8f63ff; }
.prev-tourney-button {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 2px solid #fff;
  background: rgba(12, 54, 80, 0.85);
  color: #fff;
  font-size: 17px;
  text-decoration: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
}
.prev-tourney-button:hover { background: #1C6EA4; }
.archive-strip {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  justify-content: center;
  margin: 0 auto 12px;
  padding: 7px 12px;
  max-width: 560px;
  background: rgba(60, 42, 12, 0.9);
  border: 1px solid #a07a2a;
  border-radius: 8px;
  color: #f0d28a;
  font-size: 13px;
}
.archive-label { font-weight: bold; }
.archive-btn {
  display: inline-block;
  background: rgba(12, 54, 80, 0.85);
  color: #fff;
  border: 1px solid #4a9fd8;
  border-radius: 4px;
  padding: 3px 11px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
}
.archive-btn:hover { background: #1C6EA4; }
.archive-live { border-color: #5fb86a; color: #bfe9c6; }
.archive-live:hover { background: #2f7d3f; color: #fff; }

/* Goal tracker widget */
.goal-widget { max-width: 620px; margin: 0 auto 12px; padding: 8px 14px; background: rgba(40, 30, 8, 0.7); border: 1px solid rgba(199, 146, 21, 0.4); border-radius: 8px; color: #e3f1ff; text-align: left; }
.goal-dismiss { background: none; border: none; color: #9fd0f0; font-size: 13px; cursor: pointer; opacity: 0.4; padding: 0 4px 0 0; line-height: 1; }
.goal-dismiss:hover { opacity: 1; color: #fff; }
.goal-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px; }
.goal-label { font-size: 13px; font-weight: bold; color: #ffd877; }
.goal-status { font-size: 12px; opacity: 0.7; font-variant-numeric: tabular-nums; }
.goal-profile { font-size: 11px; color: #9fd0f0; text-decoration: none; margin-left: auto; }
.goal-profile:hover { text-decoration: underline; }
.goal-rolling { font-size: 12px; margin-top: 5px; color: #6ee7a0; }
.goal-remaining { display: flex; flex-wrap: wrap; gap: 3px; margin-top: 6px; align-items: center; }
.goal-remain-label { font-size: 10px; opacity: 0.5; margin-right: 2px; }
.goal-remain-item { font-size: 10px; padding: 1px 7px; border-radius: 9px; background: rgba(255, 255, 255, 0.06); white-space: nowrap; }
.goal-remain-item.hit { background: rgba(110, 231, 160, 0.2); color: #6ee7a0; border: 1px solid rgba(110, 231, 160, 0.4); }
.goal-remain-more { font-size: 10px; opacity: 0.5; }
.goal-hint { font-size: 11px; opacity: 0.6; margin-top: 4px; font-style: italic; }

.headsup-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1100;
  background: #1f8f3a;
  color: #fff;
  text-align: center;
  padding: 10px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
  font-size: 15px;
  cursor: pointer;
  border-bottom: 2px solid #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
}
.headsup-banner .hu-dismiss { opacity: 0.75; font-size: 12px; }

/* Unscoped so it applies regardless of where the banner lands in the layout. */
.test-mode-banner {
  display: inline-block;
  background: #c0392b;
  color: #fff;
  padding: 4px 12px;
  margin: 0 0 8px;
  font-family: "Altima", monospace;
  font-size: 14px;
  border: 1px solid #fff;
}

</style>



